import React, { useState, useRef } from 'react';
import _ from 'lodash';
import { Field, ErrorMessage } from 'formik';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { colorStyle } from 'grommet-styles';
import { CircleInformation } from 'grommet-icons';

import { Box, Stack, Paragraph, Text, TextInput, Image } from 'components/Wrapped';

const StyledIcon = styled(Image)`
  ${(props) =>
    css`
      width: 20px;
      height: 20px;
      max-width: initial;
      cursor: pointer;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 16px;
    `}
`;

export const PlaceHolder = styled(Box)`
  ${(props) =>
    props.hasFocus &&
    css`
      transform: scale(0.75) translateY(-60%);
      transform-origin: left;
    `} transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const TextField = styled(TextInput)`
  background-color: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(120px);
  border: 1px solid transparent;
  border-radius: 16px;
  color-scheme: dark;
  font-size: 14px;
  padding: 33px 16px 13px;

  &:focus {
    border-color: white;
  }

  ${(props) =>
    props.hasFocus &&
    css`
      box-shadow: none;
    `}

  ${(props) =>
    props.hasAddonStart &&
    css`
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    `}

    ${(props) =>
      props.hasAddonEnd &&
      css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      `}


  ${(props) => props.hasErrors && colorStyle('border-color', 'askColor', props.theme)}
`;

const Addon = ({ background = 'background-5', content = '', isEnd = false, ...rest }) => (
  <Box
    tabIndex="-1"
    round={{ corner: isEnd ? 'right' : 'left', size: 'xxsmall' }}
    pad={{ horizontal: 'small' }}
    background={background}
    justify="center"
    flex="grow"
    {...rest}
  >
    {typeof content === 'string' ? <Paragraph>{content}</Paragraph> : content}
  </Box>
);

export const StlyedAddon = styled(Addon)`
  ${(props) =>
    props.onClick &&
    css`
      cursor: pointer;
    `};
`;

export const StyledTextInput = ({ addonStart, addonEnd, ...rest }) => {
  const [isFocused, setIsFocused] = useState(false);
  const searchInput = useRef(null);

  function focus() {
    // Explicitly focus the text input using the raw DOM API
    // Note: we're accessing "current" to get the DOM node
    searchInput.current.focus();
  }
  return (
    <Box direction="row" pad="none" round={false}>
      {addonStart && <StlyedAddon {...addonStart} />}
      <Box round={false} pad="none" fill={true}>
        <TextField
          ref={searchInput}
          focusIndicator={false}
          hasAddonStart={!_.isEmpty(addonStart)}
          hasAddonEnd={!_.isEmpty(addonEnd)}
          hasFocus={isFocused}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
          }}
          {...rest}
        />
      </Box>
      {addonEnd && <StlyedAddon onClick={focus} {...addonEnd} isEnd={true} />}
    </Box>
  );
};

const WrappedTextFieldPassword = ({
  name,
  placeholder,
  type,
  error,
  persistentPlaceholder,
  value,
  touched,
  size,
  passwordRequirements = null,
  margin = { bottom: '10px' },
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const mobilePad = touched === undefined || (error && error.length <= 52) ? '20px' : '40px';

  return (
    <Box
      pad={{ bottom: passwordRequirements ? '24px' : size === 'small' ? mobilePad : '38px' }}
      margin={margin}
      round={false}
      style={{ position: 'relative' }}
    >
      <Field name={name}>
        {({ field, form: { handleBlur, touched, errors } }) => (
          <Box pad="none">
            <Box direction="row" pad="none" round={false}>
              <Box round={false} pad="none" fill={true} style={{ position: 'relative' }}>
                <Stack anchor="left" interactiveChild="first">
                  <TextField
                    {...field}
                    {...rest}
                    type={visible ? 'text' : 'password'}
                    onFocus={() => setIsFocused(true)}
                    onBlur={(e) => {
                      handleBlur(e);
                      setIsFocused(false || !_.isEmpty(field.value));
                    }}
                    hasErrors={_.get(touched, field.name) && _.get(errors, field.name)}
                    hasFocus={isFocused}
                    focusIndicator={false}
                    placeholder={!persistentPlaceholder && placeholder}
                    value={value}
                  />
                  {persistentPlaceholder && (
                    <PlaceHolder
                      hasFocus={isFocused || field.value}
                      pad="xsmall"
                      margin={{ left: 'small' }}
                      round={false}
                      background="transparent"
                    >
                      <Text color="#B4B6B7" size="small" weight={500}>
                        {placeholder}
                      </Text>
                    </PlaceHolder>
                  )}
                </Stack>
                <StyledIcon
                  pad="none"
                  width="20px"
                  onClick={() => setVisible(!visible)}
                  src={visible ? '/assets/img/icons/view.svg' : '/assets/img/icons/hide.svg'}
                />
              </Box>
            </Box>
            {passwordRequirements ? (
              <Box pad="none" gap="10px" margin={{ top: '10px' }}>
                {passwordRequirements.map((item) => (
                  <Box key={item.label} align="center" direction="row" pad="none" gap="8px">
                    <Box
                      background="rgba(255, 255, 255, 0.1)"
                      border={{
                        side: 'all',
                        size: '1px',
                        color: 'rgba(255, 255, 255, 0.16)',
                      }}
                      height="20px"
                      pad="none"
                      round="50%"
                      align="center"
                      justify="center"
                      width="20px"
                    >
                      {item.test(field.value) && <Box pad="none" background="#568677" height="10px" round="50%" width="10px" />}
                    </Box>
                    <Text color="#B4B6B7" size="12px" weight={500}>
                      {item.label}
                    </Text>
                  </Box>
                ))}
              </Box>
            ) : (
              <ErrorMessage
                name={name}
                render={(msg) => (
                  <Box pad="none" align="center" direction="row" gap="4px" style={{ position: 'absolute', top: '73px' }}>
                    <CircleInformation color="#E56150" size="16px" />
                    <Text color="white" size="13px">
                      {msg}
                    </Text>
                  </Box>
                )}
              />
            )}
          </Box>
        )}
      </Field>
    </Box>
  );
};

WrappedTextFieldPassword.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  persistentPlaceholder: PropTypes.bool,
};

WrappedTextFieldPassword.defaultProps = {
  persistentPlaceholder: true,
};

export default WrappedTextFieldPassword;
